import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItemIcon, ListItemText, MenuItem, Theme, Tooltip } from '@mui/material';
import { ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import {
  QlfConfirmationDialog,
  QlfConfirmationDialogProps,
  useAlerts,
  useConfirmationDialog,
} from '@qualifio/ui-commons';
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  blockAndUnblockConfirms: {
    display: 'inline-block',
    marginRight: spacing(1),
  },
}));

export interface BlockAndUnblockMemberButtonsProps {
  profile: ProfileAttributes;
  programId: string;
}

export interface BlockMemberButtonsProps extends BlockAndUnblockMemberButtonsProps {
  onBlock?: () => Promise<void> | void;
  onButtonClick: () => void;
  confirmationDialogState: QlfConfirmationDialogProps;
  setConfirmationDialogState: (props: QlfConfirmationDialogProps) => void;
}

export function BlockMemberButton({
  profile,
  programId,
  onBlock,
  onButtonClick,
  confirmationDialogState,
  setConfirmationDialogState,
}: BlockMemberButtonsProps) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { features } = useFeatures();
  const { enabled: blockingEnabled } = features[Feature.BLOCKING];

  const { profileApi } = useApi();

  const displayName = useMemo(() => `${profile?.firstName} ${profile?.lastName}`, [profile]);

  const blockMember = useCallback(async () => {
    try {
      await profileApi.blockMember(programId, profile.id);
      notify(t('profiles.actions.block.success', { member: displayName }), 'success');
      await onBlock?.();
    } catch (error) {
      console.error(error);
      notify(t('generic.error', { error }), 'error');
    }
  }, [profile, programId, onBlock, displayName]);

  const showDialog = () => {
    setConfirmationDialogState({
      ...confirmationDialogState,
      open: true,
      color: 'primary',
      title: t('profiles.actions.block.confirm.title', { member: displayName }),
      content: t('profiles.actions.block.confirm.message'),
      confirmButtonColor: 'error',
      confirmButtonText: t('profiles.actions.block.confirm.button'),
      cancelButtonText: t('publish.confirm.button.cancel'),
      onConfirm: blockMember,
    });
    onButtonClick();
  };

  return (
    <Tooltip title={blockingEnabled ? '' : t('shared.action.permission.required')}>
      <div>
        <MenuItem onClick={showDialog}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faBan} />
          </ListItemIcon>
          <ListItemText>{t('profiles.actions.block.button')}</ListItemText>
        </MenuItem>
      </div>
    </Tooltip>
  );
}

export interface UnblockMemberButtonsProps extends BlockAndUnblockMemberButtonsProps {
  onUnblock?: () => Promise<void> | void;
}

export interface UnblockMemberConfirmProps extends UnblockMemberButtonsProps {
  childFactory: (loading: boolean, disabled: boolean, onConfirm: () => void) => ReactNode;
}

export function UnblockMemberConfirm({
  childFactory,
  profile,
  programId,
  onUnblock,
}: UnblockMemberConfirmProps) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const { features } = useFeatures();
  const { enabled: unblockEnabled } = features[Feature.UNBLOCKING];

  const { profileApi } = useApi();

  const displayName = useMemo(() => `${profile?.firstName} ${profile?.lastName}`, [profile]);

  const unblockMember = useCallback(async () => {
    setLoading(true);
    try {
      await profileApi.unblockMember(programId, profile.id);
      notify(t('profiles.actions.unblock.success', { member: displayName }), 'success');
      await onUnblock?.();
    } catch (error) {
      notify(t('generic.error', { error }), 'error');
    } finally {
      setLoading(false);
    }
  }, [profile, programId]);

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();
  const openDialog = () => {
    setConfirmationDialogState({
      title: t('profiles.actions.unblock.confirm.title', { member: displayName }),
      content: t('profiles.actions.unblock.confirm.message'),
      confirmButtonText: t('profiles.actions.unblock.confirm.button'),
      color: 'primary',
      confirmButtonColor: 'success',
      confirmButtonIcon: null,
      open: true,
      onConfirm: () => unblockMember(),
    });
  };

  return (
    <Tooltip title={unblockEnabled ? '' : t('shared.action.permission.required')}>
      <div>
        <QlfConfirmationDialog {...confirmationDialogState} />
        {childFactory(loading, !unblockEnabled, openDialog)}
      </div>
    </Tooltip>
  );
}
